import React, { useRef } from "react"
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react"

type Props = {
  children: React.ReactNode
  className?: string
  ariaLabel?: string
}

const LandingCarousel: React.FC<Props> = ({ children, className = "", ariaLabel = "Carousel" }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeftStart = useRef(0)

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current
    if (!container) return

    const track = container.firstElementChild as HTMLElement | null
    const firstItem = (track?.firstElementChild ?? container.firstElementChild) as HTMLElement | null
    if (!firstItem) return

    const gap = parseFloat(getComputedStyle(track ?? container).columnGap || "0") || 0
    const scrollAmount = firstItem.offsetWidth + gap

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault()
      scroll("left")
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      scroll("right")
    }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select")) return

    const container = scrollRef.current
    if (!container) return

    isDragging.current = true
    startX.current = e.clientX
    scrollLeftStart.current = container.scrollLeft
    container.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollRef.current) return

    const delta = e.clientX - startX.current
    scrollRef.current.scrollLeft = scrollLeftStart.current - delta
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollRef.current) return

    isDragging.current = false
    scrollRef.current.releasePointerCapture(e.pointerId)
  }

  return (
    <div className={className}>
      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="w-full overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] focus:outline-none focus-visible:ring-2 focus-visible:ring-dc-primary-900 focus-visible:ring-offset-2 [&::-webkit-scrollbar]:hidden"
      >
        <div className="mx-auto flex w-max min-w-full justify-center gap-6 snap-x snap-mandatory">
          {children}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => scroll("left")}
          aria-label="Previous slide"
          className="flex size-11 items-center justify-center rounded-full border border-[#ebebeb] bg-white text-[#0d0d0d] shadow-sm transition-opacity hover:opacity-80"
        >
          <IconChevronLeft size={22} stroke={1.5} />
        </button>
        <button
          type="button"
          onClick={() => scroll("right")}
          aria-label="Next slide"
          className="flex size-11 items-center justify-center rounded-full border border-[#ebebeb] bg-white text-[#0d0d0d] shadow-sm transition-opacity hover:opacity-80"
        >
          <IconChevronRight size={22} stroke={1.5} />
        </button>
      </div>
    </div>
  )
}

export default LandingCarousel
